import React from "react";
import { Button } from "@/dentlab/src/components/ui/button";
import { AlertCircle, X } from "lucide-react";
import { cn } from "../../lib/utils";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";

interface TabPanelCardProps {
	title: string;
	icon: React.ReactNode;
	headerButton?:
		| React.ReactNode
		| {
				label: string;
				icon?: React.ReactNode;
				onClick: () => void;
		  };
	onClose: () => void;
	children: React.ReactNode;
	style?: React.CSSProperties;
	headerColor?: string;
	headerTextColorClass?: string;
	incompleteFields?: string[];
	compact?: boolean;
	closeTestId?: string;
}

export const TabPanelCard: React.FC<TabPanelCardProps> = ({
	title,
	icon,
	headerButton,
	onClose,
	children,
	style,
	headerColor,
	headerTextColorClass = "text-gray-800",
	incompleteFields,
	compact = false,
	closeTestId,
}) => {
	return (
		<div
			className="flex flex-col bg-white border-b border-l border-r"
			style={style}
		>
			{/* Tab panel header */}
			<div
				className={cn(
					"flex items-center justify-between border-b",
					compact ? "px-1.5 py-1.5 gap-1" : "px-3 py-2 gap-2",
					!headerColor && "bg-gray-50"
				)}
				style={{ backgroundColor: headerColor || undefined }}
			>
				<span
					className={cn(
						"font-medium text-sm flex items-center min-w-0",
						compact ? "gap-1" : "gap-2",
						headerTextColorClass
					)}
				>
					{icon}
					{!compact && (
						<span className="truncate" title={title}>
							{title}
						</span>
					)}
					{incompleteFields && incompleteFields.length > 0 && (
						<Tooltip>
							<TooltipTrigger asChild>
								<AlertCircle className="h-4 w-4 text-white bg-amber-500 rounded-full" />
							</TooltipTrigger>
							<TooltipContent>
								<p>
									Erforderliche Felder:{" "}
									{incompleteFields.join(", ")}
								</p>
							</TooltipContent>
						</Tooltip>
					)}
				</span>
				<div
					className={cn(
						"flex items-center",
						compact ? "gap-0.5" : "gap-2"
					)}
				>
					{headerButton &&
						(React.isValidElement(headerButton) ? (
							headerButton
						) : (
							<Button
								size="sm"
								onClick={(headerButton as any).onClick}
								className="gap-1"
							>
								{(headerButton as any).icon}
								{(headerButton as any).label}
							</Button>
						))}
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="h-6 w-6"
						aria-label={`${title} schliessen`}
						data-testid={closeTestId}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Tab panel content */}
			<div className="flex-1 overflow-auto">{children}</div>
		</div>
	);
};
